import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useSubscription } from '../../context/SubscriptionContext';
import {
  Crown,
  Check,
  Sparkles,
  Building2,
  Plus,
  CreditCard,
  Shield,
  Calendar,
  Download,
  AlertCircle,
  X,
  ArrowRight,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { toast } from 'sonner';

type Platform = 'petso' | 'petso-pro' | 'farmso';

interface SubscriptionSettingsProps {
  platform: Platform;
  currentPlan?: string;
  billingCycle?: 'monthly' | 'annual';
  nextBillingDate?: string;
  billingEnabled?: boolean;
}

export function SubscriptionSettings({
  platform,
  currentPlan = 'free',
  billingCycle: initialBillingCycle = 'annual',
  nextBillingDate = '2025-01-22',
  billingEnabled = false,
}: SubscriptionSettingsProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { getProductConfig, getPlanById } = useSubscription();
  const [autoRenew, setAutoRenew] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(
    initialBillingCycle
  );

  // Get dynamic product configuration from context
  const productConfig = getProductConfig(platform);

  const platformConfig = {
    petso: {
      name: 'Petso',
      color: '#FF6B9D',
      plans: {
        free: { name: 'Free', price: 0, annualPrice: 0 },
        freemium: { name: 'Freemium', price: 9.99, annualPrice: 95.9 },
        paid: { name: 'Paid', price: 9.99, annualPrice: 95.9 },
      },
    },
    'petso-pro': {
      name: 'Petso Pro',
      color: '#6366F1',
      plans: {
        free: { name: 'Free', price: 0, annualPrice: 0 },
        starter: { name: 'Starter Freemium', price: 79.99, annualPrice: 767.9 },
        professional: {
          name: 'Professional',
          price: 79.99,
          annualPrice: 767.9,
        },
        enterprise: { name: 'Enterprise', price: 199.99, annualPrice: 1919.9 },
      },
    },
    farmso: {
      name: 'Farmso',
      color: '#16A34A',
      plans: {
        free: { name: 'Free', price: 0, annualPrice: 0 },
        starter: { name: 'Starter Freemium', price: 99.99, annualPrice: 959.9 },
        professional: {
          name: 'Professional',
          price: 99.99,
          annualPrice: 959.9,
        },
        enterprise: { name: 'Enterprise', price: 299.99, annualPrice: 2879.9 },
      },
    },
  };

  // Helper function to get plan info dynamically
  const getPlanInfo = (planKey: string) => {
    // Try to get from context first
    const contextPlan = getPlanById(platform, planKey);
    if (contextPlan) {
      return {
        name: contextPlan.name,
        price: contextPlan.monthlyPrice,
        annualPrice: contextPlan.annualPrice,
        freemiumMonths: contextPlan.freemiumMonths,
        features: contextPlan.features,
      };
    }
    // Fall back to static config
    const staticPlan =
      platformConfig[platform].plans[
        planKey as keyof (typeof platformConfig)[typeof platform]['plans']
      ];
    return staticPlan || { name: planKey, price: 0, annualPrice: 0 };
  };

  const config = platformConfig[platform];
  const currentPlanInfo = getPlanInfo(currentPlan);
  const isPaidPlan = currentPlan !== 'free';

  // Build dynamic plans object from context
  const dynamicPlans = productConfig
    ? Object.fromEntries(
        productConfig.plans.map((plan) => [
          plan.id.replace(`${platform}-`, ''), // Remove platform prefix to get plan key
          getPlanInfo(plan.id.replace(`${platform}-`, '')),
        ])
      )
    : config.plans;

  const handleDownloadInvoice = () => {
    toast.success(t('settings.subscription.invoiceDownloaded'));
  };

  const handleCancelSubscription = () => {
    setShowCancelModal(false);
    toast.success(t('settings.subscription.subscriptionCancelled'));
  };

  const handleSelectPlan = (planKey: string) => {
    if (!billingEnabled) {
      toast.info(t('settings.subscription.paidPlansSoonToast'));
      return;
    }
    toast.success(
      t('settings.subscription.upgradingToPlan', {
        plan: config.plans[planKey as keyof typeof config.plans].name,
      })
    );
  };

  const getPlanIcon = (planKey: string) => {
    if (planKey === 'free') return Crown;
    if (planKey === 'basic' || planKey === 'starter') return Plus;
    if (planKey === 'premium' || planKey === 'professional') return Sparkles;
    if (planKey === 'family' || planKey === 'enterprise') return Building2;
    return Check;
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-gray-600 dark:text-white/70">
            {t('settings.subscription.manageBilling')}
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {/* Current Plan */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="dark:text-white">
                  <CreditCard className="w-5 h-5 inline mr-2" />
                  {t('settings.subscription.currentPlan')}
                </CardTitle>
                <CardDescription>
                  {t('settings.subscription.yourActiveSubscriptionDetails')}
                </CardDescription>
              </div>
              {!billingEnabled && (
                <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                  <Shield className="w-3 h-3 mr-1" />
                  {t('settings.subscription.freeAccess')}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div
              className="p-6 rounded-xl border dark:border-white/8"
              style={{
                borderColor: config.color,
                backgroundColor: `${config.color}05`,
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3
                      className="text-[28px] dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {currentPlanInfo.name}
                    </h3>
                    <Badge
                      style={{ backgroundColor: config.color, color: 'white' }}
                    >
                      {t('settings.subscription.active')}
                    </Badge>
                  </div>

                  {isPaidPlan && billingEnabled ? (
                    <div className="space-y-2">
                      <div className="flex items-baseline gap-2">
                        <span
                          className="text-[32px] dark:text-white"
                          style={{
                            fontFamily: 'Sora, sans-serif',
                            fontWeight: 700,
                          }}
                        >
                          $
                          {billingCycle === 'monthly'
                            ? currentPlanInfo.price
                            : currentPlanInfo.annualPrice}
                        </span>
                        <span className="text-base text-gray-600 dark:text-white/70">
                          /
                          {billingCycle === 'monthly'
                            ? t('settings.subscription.month')
                            : t('settings.subscription.year')}
                        </span>
                        {billingCycle === 'annual' && (
                          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                            {t('settings.subscription.save20')}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-white/70">
                        <Calendar className="w-4 h-4" />
                        {t('settings.subscription.nextBilling', {
                          date: nextBillingDate,
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div
                        className="text-[32px] text-green-600 dark:text-green-400"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 700,
                        }}
                      >
                        {currentPlan === 'free'
                          ? t('settings.subscription.free')
                          : t('settings.subscription.includedFree')}
                      </div>
                      {!billingEnabled && isPaidPlan && (
                        <div className="text-sm text-gray-600 dark:text-white/70">
                          {t('settings.subscription.regularPrice', {
                            price:
                              billingCycle === 'monthly'
                                ? currentPlanInfo.price
                                : currentPlanInfo.annualPrice,
                            period:
                              billingCycle === 'monthly'
                                ? t('settings.subscription.mo')
                                : t('settings.subscription.yr'),
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!billingEnabled && (
                <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 mt-0.5 text-green-600 dark:text-green-400" />
                    <div className="text-sm text-green-800 dark:text-green-300">
                      <strong>
                        {t('settings.subscription.launchPeriodSpecial')}
                      </strong>{' '}
                      {t('settings.subscription.launchPeriodSpecialDesc', {
                        plan: currentPlanInfo.name,
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* All Subscription Plans */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3
                className="text-2xl dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('settings.subscription.allSubscriptionPlans')}
              </h3>
              <p className="text-sm mt-1 text-gray-600 dark:text-white/70">
                {billingEnabled
                  ? t('settings.subscription.choosePlanFitsNeeds')
                  : t('settings.subscription.paidPlansSoonEnjoyFree')}
              </p>
            </div>

            {/* Billing Toggle */}
            <div className="flex items-center gap-3 p-2 rounded-xl border border-gray-200 dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16]">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white dark:bg-[#16151D] shadow-sm text-purple-900 dark:text-white'
                    : 'bg-transparent text-gray-600 dark:text-white/50'
                }`}
                style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: billingCycle === 'monthly' ? 600 : 400,
                }}
              >
                {t('settings.subscription.monthly')}
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                  billingCycle === 'annual'
                    ? 'bg-white dark:bg-[#16151D] shadow-sm text-purple-900 dark:text-white'
                    : 'bg-transparent text-gray-600 dark:text-white/50'
                }`}
                style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: billingCycle === 'annual' ? 600 : 400,
                }}
              >
                {t('settings.subscription.annual')}
                <Badge
                  className={`ml-1 ${
                    billingCycle === 'annual'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-gray-200 dark:bg-white/5 text-gray-600 dark:text-white/50'
                  }`}
                >
                  {t('settings.subscription.save20')}
                </Badge>
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div
            className={`grid gap-6 ${
              platform === 'petso'
                ? 'md:grid-cols-2 lg:grid-cols-3'
                : 'md:grid-cols-2 lg:grid-cols-4'
            }`}
          >
            {Object.entries(dynamicPlans).map(([planKey, plan]) => {
              const PlanIcon = getPlanIcon(planKey);
              const isCurrentPlan = planKey === currentPlan;
              const planInfo = getPlanInfo(planKey);
              const features: string[] = (planInfo as any).features || [];
              const isTopPlan =
                planKey === 'family' ||
                (planKey === 'enterprise' &&
                  platform !== 'petso-pro' &&
                  platform !== 'farmso') ||
                (planKey === 'professional' &&
                  (platform === 'petso-pro' || platform === 'farmso'));

              return (
                <Card
                  key={planKey}
                  className={`relative overflow-hidden transition-all ${
                    isCurrentPlan ? 'ring-2 ring-offset-2' : 'hover:shadow-lg'
                  }`}
                  style={{
                    ringColor: isCurrentPlan ? config.color : undefined,
                    borderColor: isTopPlan ? config.color : '#E2DFEA',
                  }}
                >
                  {isTopPlan && (
                    <div
                      className="absolute top-0 left-0 right-0 h-1"
                      style={{ backgroundColor: config.color }}
                    />
                  )}

                  <CardHeader>
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className="p-3 rounded-xl"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        <PlanIcon
                          className="w-6 h-6"
                          style={{ color: config.color }}
                        />
                      </div>
                      {isCurrentPlan && (
                        <Badge
                          style={{
                            backgroundColor: config.color,
                            color: 'white',
                          }}
                        >
                          {t('settings.subscription.current')}
                        </Badge>
                      )}
                      {isTopPlan && !isCurrentPlan && (
                        <Badge
                          style={{
                            backgroundColor: '#FEF3C7',
                            color: '#92400E',
                          }}
                        >
                          {t('settings.subscription.popular')}
                        </Badge>
                      )}
                    </div>

                    <CardTitle
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        color: theme === 'dark' ? '#FFFFFF' : '#2D2F7E',
                        fontWeight: planKey === 'enterprise' ? 700 : 600,
                      }}
                    >
                      {plan.name || planKey}
                    </CardTitle>

                    <div className="mt-4">
                      {planKey === 'free' ? (
                        <div>
                          <span
                            className="text-green-600 dark:text-green-400"
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                              fontSize: '32px',
                            }}
                          >
                            {t('settings.subscription.free')}
                          </span>
                          <p className="text-sm mt-1 text-gray-500 dark:text-white/50">
                            {t('settings.subscription.forever')}
                          </p>
                        </div>
                      ) : planKey === 'freemium' && platform === 'petso' ? (
                        <div>
                          <div className="mb-2">
                            <Badge
                              style={{
                                backgroundColor: '#FEF3C7',
                                color: '#92400E',
                              }}
                            >
                              {t('settings.subscription.freeFor3Months')}
                            </Badge>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-[#2D2F7E] dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                                fontSize: '32px',
                              }}
                            >
                              $
                              {billingCycle === 'monthly'
                                ? plan.price
                                : plan.annualPrice}
                            </span>
                            <span
                              className="text-gray-500 dark:text-white/50"
                              style={{ fontSize: '14px' }}
                            >
                              /
                              {billingCycle === 'monthly'
                                ? t('settings.subscription.mo')
                                : t('settings.subscription.yr')}
                            </span>
                          </div>
                          {billingCycle === 'annual' ? (
                            <p className="text-xs mt-1 text-green-600 dark:text-green-400">
                              {t('settings.subscription.savePerYear', {
                                amount: (
                                  plan.price * 12 -
                                  plan.annualPrice
                                ).toFixed(2),
                              })}
                            </p>
                          ) : (
                            <p className="text-xs mt-1 text-gray-500 dark:text-white/50">
                              {t('settings.subscription.thenPerMonth', {
                                price: plan.price,
                              })}
                            </p>
                          )}
                        </div>
                      ) : planKey === 'starter' && platform === 'petso-pro' ? (
                        <div>
                          <div className="mb-2">
                            <Badge
                              style={{
                                backgroundColor: '#FEF3C7',
                                color: '#92400E',
                              }}
                            >
                              {t('settings.subscription.freeFor3Months')}
                            </Badge>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-[#2D2F7E] dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                                fontSize: '32px',
                              }}
                            >
                              $
                              {billingCycle === 'monthly'
                                ? plan.price
                                : plan.annualPrice}
                            </span>
                            <span
                              className="text-gray-500 dark:text-white/50"
                              style={{ fontSize: '14px' }}
                            >
                              /
                              {billingCycle === 'monthly'
                                ? t('settings.subscription.mo')
                                : t('settings.subscription.yr')}
                            </span>
                          </div>
                          {billingCycle === 'annual' ? (
                            <p className="text-xs mt-1 text-green-600 dark:text-green-400">
                              {t('settings.subscription.savePerYear', {
                                amount: (
                                  plan.price * 12 -
                                  plan.annualPrice
                                ).toFixed(2),
                              })}
                            </p>
                          ) : (
                            <p className="text-xs mt-1 text-gray-500 dark:text-white/50">
                              {t('settings.subscription.thenPerMonth', {
                                price: plan.price,
                              })}
                            </p>
                          )}
                        </div>
                      ) : planKey === 'starter' && platform === 'farmso' ? (
                        <div>
                          <div className="mb-2">
                            <Badge
                              style={{
                                backgroundColor: '#FEF3C7',
                                color: '#92400E',
                              }}
                            >
                              {t('settings.subscription.freeFor3Months')}
                            </Badge>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-[#2D2F7E] dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                                fontSize: '32px',
                              }}
                            >
                              $
                              {billingCycle === 'monthly'
                                ? plan.price
                                : plan.annualPrice}
                            </span>
                            <span
                              className="text-gray-500 dark:text-white/50"
                              style={{ fontSize: '14px' }}
                            >
                              /
                              {billingCycle === 'monthly'
                                ? t('settings.subscription.mo')
                                : t('settings.subscription.yr')}
                            </span>
                          </div>
                          {billingCycle === 'annual' ? (
                            <p className="text-xs mt-1 text-green-600 dark:text-green-400">
                              {t('settings.subscription.savePerYear', {
                                amount: (
                                  plan.price * 12 -
                                  plan.annualPrice
                                ).toFixed(2),
                              })}
                            </p>
                          ) : (
                            <p className="text-xs mt-1 text-gray-500 dark:text-white/50">
                              {t('settings.subscription.thenPerMonth', {
                                price: plan.price,
                              })}
                            </p>
                          )}
                        </div>
                      ) : planKey === 'paid' && platform === 'petso' ? (
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-[#2D2F7E] dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                                fontSize: '32px',
                              }}
                            >
                              $
                              {billingCycle === 'monthly'
                                ? plan.price
                                : plan.annualPrice}
                            </span>
                            <span
                              className="text-gray-500 dark:text-white/50"
                              style={{ fontSize: '14px' }}
                            >
                              /
                              {billingCycle === 'monthly'
                                ? t('settings.subscription.mo')
                                : t('settings.subscription.yr')}
                            </span>
                          </div>
                          {billingCycle === 'annual' && (
                            <p className="text-xs mt-1 text-green-600 dark:text-green-400">
                              {t('settings.subscription.savePerYear', {
                                amount: (
                                  plan.price * 12 -
                                  plan.annualPrice
                                ).toFixed(2),
                              })}
                            </p>
                          )}
                        </div>
                      ) : (planKey === 'professional' ||
                          planKey === 'enterprise') &&
                        platform === 'petso-pro' ? (
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-[#2D2F7E] dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                                fontSize: '32px',
                              }}
                            >
                              $
                              {billingCycle === 'monthly'
                                ? plan.price
                                : plan.annualPrice}
                            </span>
                            <span
                              className="text-gray-500 dark:text-white/50"
                              style={{ fontSize: '14px' }}
                            >
                              /
                              {billingCycle === 'monthly'
                                ? t('settings.subscription.mo')
                                : t('settings.subscription.yr')}
                            </span>
                          </div>
                          {billingCycle === 'annual' && (
                            <p className="text-xs mt-1 text-green-600 dark:text-green-400">
                              {t('settings.subscription.savePerYear', {
                                amount: (
                                  plan.price * 12 -
                                  plan.annualPrice
                                ).toFixed(2),
                              })}
                            </p>
                          )}
                        </div>
                      ) : (planKey === 'professional' ||
                          planKey === 'enterprise') &&
                        platform === 'farmso' ? (
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-[#2D2F7E] dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                                fontSize: '32px',
                              }}
                            >
                              $
                              {billingCycle === 'monthly'
                                ? plan.price
                                : plan.annualPrice}
                            </span>
                            <span
                              className="text-gray-500 dark:text-white/50"
                              style={{ fontSize: '14px' }}
                            >
                              /
                              {billingCycle === 'monthly'
                                ? t('settings.subscription.mo')
                                : t('settings.subscription.yr')}
                            </span>
                          </div>
                          {billingCycle === 'annual' && (
                            <p className="text-xs mt-1 text-green-600 dark:text-green-400">
                              {t('settings.subscription.savePerYear', {
                                amount: (
                                  plan.price * 12 -
                                  plan.annualPrice
                                ).toFixed(2),
                              })}
                            </p>
                          )}
                        </div>
                      ) : !billingEnabled ? (
                        <div>
                          <span
                            className="text-green-600 dark:text-green-400"
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                              fontSize: '24px',
                            }}
                          >
                            {t('settings.subscription.comingSoon')}
                          </span>
                          <p className="text-sm mt-1 text-gray-500 dark:text-white/50">
                            {t('settings.subscription.freeForNow')}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-[#2D2F7E] dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                                fontSize: '32px',
                              }}
                            >
                              $
                              {billingCycle === 'monthly'
                                ? plan.price
                                : plan.annualPrice}
                            </span>
                            <span
                              className="text-gray-500 dark:text-white/50"
                              style={{ fontSize: '14px' }}
                            >
                              /
                              {billingCycle === 'monthly'
                                ? t('settings.subscription.mo')
                                : t('settings.subscription.yr')}
                            </span>
                          </div>
                          {billingCycle === 'annual' && (
                            <p className="text-xs mt-1 text-green-600 dark:text-green-400">
                              {t('settings.subscription.savePerYear', {
                                amount: (
                                  plan.price * 12 -
                                  plan.annualPrice
                                ).toFixed(2),
                              })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="space-y-3 mb-6">
                      {features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check
                            className="w-4 h-4 mt-0.5 flex-shrink-0"
                            style={{ color: config.color }}
                          />
                          <span className="text-sm text-gray-600 dark:text-white/70">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    {isCurrentPlan ? (
                      <Button className="w-full" variant="outline" disabled>
                        {t('settings.subscription.currentPlan')}
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        style={{
                          backgroundColor:
                            planKey === 'free' ? 'white' : config.color,
                          color: planKey === 'free' ? config.color : 'white',
                          border:
                            planKey === 'free'
                              ? `2px solid ${config.color}`
                              : 'none',
                        }}
                        onClick={() => handleSelectPlan(planKey)}
                        disabled={!billingEnabled && planKey !== 'free'}
                      >
                        {planKey === 'free'
                          ? t('settings.subscription.downgrade')
                          : billingEnabled
                            ? t('settings.subscription.upgrade')
                            : t('settings.subscription.comingSoon')}
                        {planKey !== 'free' && (
                          <ArrowRight className="w-4 h-4 ml-2" />
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Info Message */}
          {!billingEnabled && (
            <div className="mt-6 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 mt-0.5 text-blue-600 dark:text-blue-400" />
                <div className="flex-1">
                  <p className="text-sm text-blue-900 dark:text-blue-200">
                    {t('settings.subscription.allPlansFreeBanner')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Billing Settings */}
        {isPaidPlan && billingEnabled && (
          <Card>
            <CardHeader>
              <CardTitle
                style={{ fontFamily: 'Sora, sans-serif', color: '#2D2F7E' }}
              >
                {t('settings.subscription.billingSettings', {
                  defaultValue: 'Billing Settings',
                })}
              </CardTitle>
              <CardDescription>
                {t('settings.subscription.manageBillingPreferences', {
                  defaultValue: 'Manage billing preferences and auto-renewal',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="flex items-center justify-between p-4 rounded-xl border"
                style={{ borderColor: '#E2DFEA' }}
              >
                <div>
                  <Label>
                    {t('settings.subscription.autoRenewal', {
                      defaultValue: 'Auto-Renewal',
                    })}
                  </Label>
                  <p className="text-xs mt-1" style={{ color: '#8B8A92' }}>
                    {t('settings.subscription.autoRenewalDescription', {
                      defaultValue:
                        'Automatically renew subscription at the end of each billing period',
                    })}
                  </p>
                </div>
                <Switch checked={autoRenew} onCheckedChange={setAutoRenew} />
              </div>

              <div
                className="p-4 rounded-xl border"
                style={{ borderColor: '#E2DFEA' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <Label>
                    {t('settings.subscription.billingCycle', {
                      defaultValue: 'Billing Cycle',
                    })}
                  </Label>
                  <Badge variant="outline">
                    {billingCycle === 'monthly'
                      ? t('settings.subscription.monthly', {
                          defaultValue: 'Monthly',
                        })
                      : t('settings.subscription.annualSave20', {
                          defaultValue: 'Annual (Save 20%)',
                        })}
                  </Badge>
                </div>
                <p className="text-xs" style={{ color: '#8B8A92' }}>
                  {t('settings.subscription.switchBillingCycle', {
                    defaultValue:
                      'Switch to {{cycle}} billing on your next renewal',
                    cycle:
                      billingCycle === 'monthly'
                        ? t('settings.subscription.annualLower', {
                            defaultValue: 'annual',
                          })
                        : t('settings.subscription.monthlyLower', {
                            defaultValue: 'monthly',
                          }),
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Method */}
        {isPaidPlan && billingEnabled && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ fontFamily: 'Sora, sans-serif', color: '#2D2F7E' }}
                  >
                    {t('settings.subscription.paymentMethod', {
                      defaultValue: 'Payment Method',
                    })}
                  </CardTitle>
                  <CardDescription>
                    {t('settings.subscription.managePaymentInformation', {
                      defaultValue: 'Manage your payment information',
                    })}
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm">
                  {t('settings.subscription.updateCard', {
                    defaultValue: 'Update Card',
                  })}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div
                className="p-4 rounded-xl border"
                style={{ borderColor: '#E2DFEA' }}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-gray-100">
                    <CreditCard
                      className="w-6 h-6"
                      style={{ color: '#2D2F7E' }}
                    />
                  </div>
                  <div className="flex-1">
                    <div
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                        color: '#2D2F7E',
                      }}
                    >
                      •••• •••• •••• 4242
                    </div>
                    <div className="text-sm" style={{ color: '#8B8A92' }}>
                      {t('settings.subscription.expiresDate', {
                        defaultValue: 'Expires {{date}}',
                        date: '12/2025',
                      })}
                    </div>
                  </div>
                  <Badge
                    style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    {t('settings.subscription.verified', {
                      defaultValue: 'Verified',
                    })}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billing History */}
        {isPaidPlan && billingEnabled && (
          <Card>
            <CardHeader>
              <CardTitle
                style={{ fontFamily: 'Sora, sans-serif', color: '#2D2F7E' }}
              >
                {t('settings.subscription.billingHistory', {
                  defaultValue: 'Billing History',
                })}
              </CardTitle>
              <CardDescription>
                {t('settings.subscription.viewDownloadPastInvoices', {
                  defaultValue: 'View and download past invoices',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  {
                    date: '2024-12-01',
                    amount:
                      billingCycle === 'monthly'
                        ? currentPlanInfo.price
                        : currentPlanInfo.annualPrice,
                    status: 'Paid',
                  },
                  {
                    date: '2024-11-01',
                    amount:
                      billingCycle === 'monthly'
                        ? currentPlanInfo.price
                        : currentPlanInfo.annualPrice,
                    status: 'Paid',
                  },
                  {
                    date: '2024-10-01',
                    amount:
                      billingCycle === 'monthly'
                        ? currentPlanInfo.price
                        : currentPlanInfo.annualPrice,
                    status: 'Paid',
                  },
                ].map((invoice, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-4 rounded-xl border"
                    style={{ borderColor: '#E2DFEA' }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-gray-50">
                        <Download
                          className="w-4 h-4"
                          style={{ color: '#8B8A92' }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontFamily: 'Sora, sans-serif',
                            fontWeight: 600,
                            color: '#2D2F7E',
                          }}
                        >
                          ${invoice.amount}
                        </div>
                        <div className="text-sm" style={{ color: '#8B8A92' }}>
                          {invoice.date}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}
                      >
                        {invoice.status}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDownloadInvoice}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cancel Subscription */}
        {isPaidPlan && billingEnabled && (
          <Card>
            <CardHeader>
              <CardTitle
                style={{ fontFamily: 'Sora, sans-serif', color: '#2D2F7E' }}
              >
                {t('settings.subscription.cancelSubscription', {
                  defaultValue: 'Cancel Subscription',
                })}
              </CardTitle>
              <CardDescription>
                {t('settings.subscription.downgradeToFreePlan', {
                  defaultValue: 'Downgrade to free plan',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="p-4 rounded-xl bg-amber-50 border"
                style={{ borderColor: '#FEF3C7' }}
              >
                <div className="flex items-start gap-2 mb-4">
                  <AlertCircle
                    className="w-5 h-5 mt-0.5"
                    style={{ color: '#F59E0B' }}
                  />
                  <div className="text-sm" style={{ color: '#92400E' }}>
                    <strong>
                      {t('settings.subscription.beforeYouCancel', {
                        defaultValue: 'Before you cancel:',
                      })}
                    </strong>{' '}
                    {t('settings.subscription.cancelWarningDescription', {
                      defaultValue:
                        "You'll lose access to premium features at the end of your billing period. Your data will be preserved, but some features may be limited on the free plan.",
                    })}
                  </div>
                </div>
                <Button
                  variant="outline"
                  style={{ borderColor: '#DC2626', color: '#DC2626' }}
                  onClick={() => setShowCancelModal(true)}
                >
                  {t('settings.subscription.cancelSubscription', {
                    defaultValue: 'Cancel Subscription',
                  })}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowCancelModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2
                style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                  color: '#2D2F7E',
                  fontSize: '24px',
                }}
              >
                {t('settings.subscription.cancelSubscriptionQuestion', {
                  defaultValue: 'Cancel Subscription?',
                })}
              </h2>
              <button
                onClick={() => setShowCancelModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" style={{ color: '#8B8A92' }} />
              </button>
            </div>

            <div className="space-y-4">
              <div
                className="p-4 rounded-xl bg-amber-50 border"
                style={{ borderColor: '#FEF3C7' }}
              >
                <div className="text-sm" style={{ color: '#92400E' }}>
                  {t('settings.subscription.cancelUntilDateMessage', {
                    defaultValue:
                      "You'll keep access to {{plan}} features until {{date}}, then automatically switch to the Free plan.",
                    plan: currentPlanInfo.name,
                    date: nextBillingDate,
                  })}
                </div>
              </div>

              <div>
                <p
                  style={{
                    color: '#8B8A92',
                    fontSize: '14px',
                    marginBottom: '8px',
                  }}
                >
                  {t('settings.subscription.youWillLoseAccessTo', {
                    defaultValue: "You'll lose access to:",
                  })}
                </p>
                <ul className="space-y-2">
                  {[
                    t('settings.subscription.lossItems.premiumFeatures', {
                      defaultValue: 'Premium features',
                    }),
                    t('settings.subscription.lossItems.prioritySupport', {
                      defaultValue: 'Priority support',
                    }),
                    t('settings.subscription.lossItems.advancedAnalytics', {
                      defaultValue: 'Advanced analytics',
                    }),
                    t('settings.subscription.lossItems.increasedLimits', {
                      defaultValue: 'Increased limits',
                    }),
                  ].map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-2 text-sm"
                      style={{ color: '#DC2626' }}
                    >
                      <X className="w-4 h-4" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCancelModal(false)}
                >
                  {t('settings.subscription.keepSubscription', {
                    defaultValue: 'Keep Subscription',
                  })}
                </Button>
                <Button
                  className="flex-1"
                  style={{ backgroundColor: '#DC2626' }}
                  onClick={handleCancelSubscription}
                >
                  {t('settings.subscription.yesCancel', {
                    defaultValue: 'Yes, Cancel',
                  })}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
